import { defineStore } from 'pinia'
import { flagsmith } from '@/app/core/services/feature-flags'
import type { IFlagsmithValue } from '@flagsmith/flagsmith'

interface FlagState {
  enabled: boolean
  value: IFlagsmithValue
}

/**
 * Store reactivo que espeja el estado del SDK de Flagsmith.
 *
 * No se persiste: las flags se rehidratan siempre desde Flagsmith (que tiene
 * su propia cache en localStorage). Se sincroniza via `syncFromSdk`, llamado
 * desde el callback `onChange` del SDK cada vez que las flags cambian.
 */
export const useFeatureFlagsStore = defineStore('feature-flags', {
  state: () => {
    return {
      flags: {} as Record<string, FlagState>,
      ready: false as boolean
    }
  },

  actions: {
    syncFromSdk() {
      this.flags = flagsmith.getAllFlags() as Record<string, FlagState>
      this.ready = flagsmith.initialised ?? false
    }
  },

  getters: {
    isEnabled: state => (key: string) => state.flags[key]?.enabled ?? false,
    value: state => (key: string) => state.flags[key]?.value ?? null,
    /**
     * Decide la visibilidad de un elemento (item de menu o ruta) segun sus
     * flags declarativas:
     * - `featureFlag`: visible solo si la flag esta ON.
     * - `hideWhenFlag`: visible solo si la flag esta OFF (para modulos legacy).
     * - sin flags: siempre visible.
     */
    isVisibleForFlags:
      state =>
      (item: { featureFlag?: string; hideWhenFlag?: string }): boolean => {
        const enabled = (key: string) => state.flags[key]?.enabled ?? false
        if (item.featureFlag && !enabled(item.featureFlag)) return false
        if (item.hideWhenFlag && enabled(item.hideWhenFlag)) return false
        return true
      }
  }
})
